/**
 * Voice mode, shared by all pages (it keeps running when a new chat navigates to its own page).
 *
 * The current page registers what to do with a transcript (send it as a message) and feeds the
 * assistant's streaming reply through `follow`, which speaks it sentence by sentence. Speaking
 * while the assistant talks interrupts it (barge-in).
 */
import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { api } from "../api";
import { MicCapture } from "./capture";
import { SentenceSplitter, SpeechPlayer, speakable } from "./speech";

export type VoicePhase = "off" | "listening" | "hearing" | "transcribing" | "waiting" | "speaking";

interface VoiceState {
  phase: VoicePhase;
  error: string | null;
  level: number;
}

export const useVoiceState = create<VoiceState>(() => ({ phase: "off", error: null, level: -90 }));

type Handler = (text: string) => void | Promise<void>;

class VoiceController {
  private mic: MicCapture | null = null;
  private handler: Handler | null = null;
  private onInterrupt: (() => void) | null = null;
  private followed: { id: string; offset: number; splitter: SentenceSplitter; done: boolean } | null = null;
  voice: string | undefined;
  readonly player = new SpeechPlayer(() => this.voice);

  constructor() {
    this.player.onState = (speaking) => {
      const { phase } = useVoiceState.getState();
      if (speaking) this.set("speaking");
      else if (phase === "speaking") this.set(this.mic ? (this.followed && !this.followed.done ? "waiting" : "listening") : "off");
    };
    this.player.onError = (message) => useVoiceState.setState({ error: `Speech: ${message}` });
  }

  get active() {
    return this.mic !== null;
  }

  private set(phase: VoicePhase) {
    useVoiceState.setState({ phase });
  }

  /** What the current page does with what the user said, and how it stops a running reply. */
  register(handler: Handler, onInterrupt?: () => void) {
    this.handler = handler;
    this.onInterrupt = onInterrupt ?? null;
    return () => {
      if (this.handler === handler) {
        this.handler = null;
        this.onInterrupt = null;
      }
    };
  }

  async start() {
    if (this.mic) return;
    useVoiceState.setState({ error: null });
    const mic = new MicCapture({
      onSpeechStart: () => {
        if (this.player.speaking || (this.followed && !this.followed.done)) this.interrupt();
        this.set("hearing");
      },
      onUtterance: (wav) => void this.transcribe(wav),
      onLevel: (db) => useVoiceState.setState({ level: db }),
      // Be harder to trigger while the assistant is talking, so its own voice does not interrupt it.
      extraThreshold: () => (this.player.speaking ? 10 : 0),
    });
    try {
      await mic.start();
    } catch (e) {
      useVoiceState.setState({ error: `Microphone: ${e instanceof Error ? e.message : String(e)}`, phase: "off" });
      return;
    }
    this.mic = mic;
    this.set("listening");
  }

  stop() {
    this.mic?.stop();
    this.mic = null;
    this.player.stop();
    this.followed = null;
    this.set("off");
  }

  /** Stops speaking and asks the page to stop the reply. */
  interrupt() {
    this.player.stop();
    if (this.followed && !this.followed.done) this.onInterrupt?.();
    this.followed = null;
  }

  private async transcribe(wav: Blob) {
    this.set("transcribing");
    try {
      const form = new FormData();
      form.append("file", wav, "speech.wav");
      const r = await api.post<{ text: string }>("/api/voice/transcribe", form);
      if (!r.text) {
        this.set(this.mic ? "listening" : "off");
        return;
      }
      this.set("waiting");
      await this.handler?.(r.text);
    } catch (e) {
      useVoiceState.setState({ error: e instanceof Error ? e.message : String(e) });
      this.set(this.mic ? "listening" : "off");
    }
  }

  /** Feed the assistant reply's text so far; it is spoken as sentences complete. */
  follow(messageId: string, text: string, done: boolean) {
    if (!this.mic) return;
    if (!this.followed || this.followed.id !== messageId) {
      // Only replies that are seen while they are still being written (not older messages on the page).
      if (done) return;
      this.followed = { id: messageId, offset: 0, splitter: new SentenceSplitter(), done: false };
    }
    const f = this.followed;
    if (f.done) return;
    const clean = speakable(text);
    if (clean.length > f.offset) {
      for (const s of f.splitter.feed(clean.slice(f.offset))) this.player.say(s);
      f.offset = clean.length;
    }
    if (done) {
      for (const s of f.splitter.flush()) this.player.say(s);
      f.done = true;
      if (!this.player.speaking) this.set("listening");
    }
  }

  /** Reads one text aloud (the speaker button on a message), outside voice mode too. */
  readAloud(text: string) {
    this.player.stop();
    const sp = new SentenceSplitter(80, 20);
    for (const s of [...sp.feed(speakable(text)), ...sp.flush()]) this.player.say(s);
  }
}

export const voice = new VoiceController();

export interface VoiceStatus {
  stt: boolean;
  tts: boolean;
  voices: string[];
  default_voice: string;
}

export function useVoiceStatus() {
  return useQuery({ queryKey: ["voice-status"], queryFn: () => api.get<VoiceStatus>("/api/voice"), staleTime: 5 * 60_000 });
}
